/* eslint-disable react/no-array-index-key */
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogProps,
  List,
  ListItem,
  Notification,
  Text,
  Tooltip,
  useToast,
} from '@bitrise/bitkit';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import useStartBuild from '@/hooks/useStartBuild';
import AppDetailsPageProps from '@/pages/App/types/AppDetailsPageProps';
import BuildConfiguration, { getFormDataToSend } from '../BuildConfiguration/BuildConfiguration';
import { StartBuildType } from './types';
import getBuildParams from './utils/getBuildParams';
import getSubmitButtonState from './utils/getSubmitButtonState';

export interface StartBuildDialogProps extends Omit<DialogProps, 'title'> {
  app: AppDetailsPageProps['app'];
  buildTriggerToken: string;
  defaultBranch: string;
  pipelines?: string[];
  workflows?: string[];
  openScheduleBuildDialog: () => void;
}

const StartBuildDialog = (props: StartBuildDialogProps) => {
  const { app, buildTriggerToken, defaultBranch, isOpen, onClose, openScheduleBuildDialog } = props;

  const defaultValues: StartBuildType = useMemo(
    () => ({
      branch: defaultBranch || '',
      buildConfigType: 'basic',
      commitHash: '',
      commitMessage: '',
      environments: [],
      licensePoolId: '',
      machineTypeId: '',
      pipelineableId: '',
      secrets: [],
      tag: '',
      source: 'branch',
    }),
    [defaultBranch],
  );

  const toast = useToast();

  const { user } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const [errorMessage, setErrorMessage] = useState<ReactNode>('');

  const useFormMethods = useForm<StartBuildType>({
    defaultValues: {
      ...defaultValues,
      branch: defaultBranch,
    },
  });

  const { handleSubmit, reset, setValue, watch, resetField } = useFormMethods;

  const formData = watch();

  const { isPending: isStartLoading, mutate: startBuild } = useStartBuild(
    {
      appSlug: app.slug,
      buildTriggerToken,
    },
    {
      onSuccess: ({ data }) => {
        if (data.results?.length === 1 && data.results[0].buildUrl) {
          window.location.href = data.results[0].buildUrl;
        }
        if (data.results && data.results?.length > 1) {
          onClose();
        }
      },
      onError: (error) => {
        if (error.response?.data.errorMsg) {
          setErrorMessage(error.response?.data.errorMsg);
        } else if (error.response?.data.results?.length) {
          const resultErrors: string[] = [];
          error.response?.data.results.forEach((result) => {
            if (result.status === 'error') {
              resultErrors.push(`${result.triggeredPipeline || result.triggeredWorkflow}: ${result.message}`);
            }
          });
          if (resultErrors.length === error.response?.data.results?.length) {
            setErrorMessage(
              <>
                <Text textStyle="body/md/semibold">Failed to start builds:</Text>
                {resultErrors.map((err) => (
                  <List>
                    <ListItem>{err}</ListItem>
                  </List>
                ))}
              </>,
            );
          } else {
            onClose();
            toast({
              title: 'Failed to start builds',
              description: (
                <>
                  <Text textStyle="body/md/semibold">Failed to start builds:</Text>
                  {resultErrors.map((err) => (
                    <List>
                      <ListItem>{err}</ListItem>
                    </List>
                  ))}
                </>
              ),
              status: 'error',
              isClosable: true,
            });
          }
        } else {
          setErrorMessage(error.response?.data.message || 'Unknown error');
        }
      },
    },
  );

  const onSubmit: SubmitHandler<StartBuildType> = (data) => {
    const dataToSend = getFormDataToSend(data);
    const buildParams = getBuildParams(dataToSend);

    startBuild({ buildParams, triggeredBy: `manual-${user.username}` });
  };

  useEffect(() => {
    if (!isOpen) {
      reset({
        ...defaultValues,
      });
      setErrorMessage('');
    }

    resetField('pipelineableId');
  }, [defaultValues, isOpen, reset, resetField]);

  useEffect(() => {
    setValue('machineTypeId', '');
    setValue('stack', '');
    setValue('pipelineableId', '');
  }, [setValue]);

  const { disabledText, isSubmitDisabled } = getSubmitButtonState(formData);

  const switchScheduleBuild = () => {
    onClose();
    openScheduleBuildDialog();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Start build"
      data-testid="start-build-dialog"
      scrollBehavior="inside"
    >
      <DialogBody>
        <FormProvider {...useFormMethods}>
          <Box as="form" id="build_form" onSubmit={handleSubmit(onSubmit)}>
            <BuildConfiguration app={app} buildTriggerToken={buildTriggerToken} showcUrlCommand />
          </Box>
        </FormProvider>
      </DialogBody>
      <DialogFooter>
        {errorMessage && <Notification status="error">{errorMessage}</Notification>}
        <Button variant="tertiary" onClick={switchScheduleBuild} marginInlineEnd="auto">
          Schedule builds
        </Button>
        <Button form="build_form" onClick={onClose} variant="secondary">
          Close
        </Button>
        <Tooltip isDisabled={!disabledText} label={disabledText}>
          <Button
            form="build_form"
            isDisabled={isSubmitDisabled}
            isLoading={isStartLoading}
            type="submit"
            data-testid="start-build"
          >
            Start build
          </Button>
        </Tooltip>
      </DialogFooter>
    </Dialog>
  );
};

export default StartBuildDialog;
